/**
 * Client mirror of api/src/utils/faultCodes.js — keep the two in step, as with
 * utils/coordinates.js and utils/siteTypes.js.
 *
 * Gareth 2026-09-11: a comma-delimited box on the work-log close holding
 * several codes, so reports can later pull tickets by the codes found.
 *
 * The browser copy exists so the engineer can SEE what will be stored while
 * typing — "E101,e205" showing back as two chips, E101 and E205, is the only
 * way the comma-delimited convention explains itself on a phone in a parlour.
 */

const MAX_CODE_LENGTH = 32;
const MAX_CODES = 25;

/**
 * Parse the typed string into the canonical list: trimmed, upper-cased,
 * de-duplicated, first-seen order kept (engineers type the primary fault
 * first and that ordering is worth keeping).
 *
 * Newlines and semicolons count as separators too — that is what comes out of
 * a robot display when someone pastes.
 */
export function parseFaultCodes(input) {
  if (input === null || input === undefined) return [];
  // Each entry of a list is split too, as in the API copy: a code never
  // contains a separator, however it arrives.
  const raw = (Array.isArray(input) ? input : [input])
    .flatMap(entry => String(entry ?? '').split(/[,;\n\r]+/));

  const seen = new Set();
  const codes = [];
  for (const entry of raw) {
    const code = String(entry ?? '').trim().toUpperCase();
    if (!code || code.length > MAX_CODE_LENGTH || seen.has(code)) continue;
    seen.add(code);
    codes.push(code);
    if (codes.length >= MAX_CODES) break;
  }
  return codes;
}

/** One line, always ", ", so a round trip through parse is stable. */
export function formatFaultCodes(codes) {
  const list = Array.isArray(codes) ? codes : parseFaultCodes(codes);
  return list.length ? list.join(', ') : '';
}

/**
 * Codes found on a ticket's OTHER work logs: the read-only context shown above
 * the box while editing a log, whose own codes are in the box itself. Sorted,
 * like the ticket's rolled-up list.
 */
export function faultCodesFromOtherLogs(workLogs, logId) {
  if (!Array.isArray(workLogs)) return [];
  return [...new Set(workLogs
    .filter(log => log && log.log_id !== logId)
    .flatMap(log => parseFaultCodes(log.fault_codes)))].sort();
}

export { MAX_CODE_LENGTH, MAX_CODES };
